import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import "./ProfileEdit.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaUserAlt } from "react-icons/fa";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { storage } from "../firebase";

const ProfilePage = () => {
  const [profileImage, setProfileImage] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const [emptyFields, setEmptyFields] = useState([]);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    orgName: "",
    location: "",
    gId: "",
    phone: "",
  });

  const [userId, setUserId] = useState("");

  const fetchUserData = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/auth/login");
      return;
    }

    try {
      const response = await axios.get(
        `https://us-central1-extent-protocol-a.cloudfunctions.net/getUserByToken?token=${token}`
      );

      const {
        name,
        surname,
        city,
        phoneNumber,
        organization,
        gId,
        userId,
        email,
      } = response.data.user;

      localStorage.setItem("auth_email", email);

      const newFormData = {
        firstName: name || "",
        lastName: surname || "",
        orgName: organization || "",
        location: city || "",
        gId: gId || "",
        phone: phoneNumber || "",
      };

      setFormData(newFormData);

      setUserId(userId);
      localStorage.setItem("userId", userId);

      const imageRef = ref(storage, `pictures/${userId}.png`);
      try {
        const url = await getDownloadURL(imageRef);
        setProfileImage(url);
        localStorage.setItem("profileImage", url);
      } catch (error) {
        console.warn("Profile image not found or failed to load:", error);
        setProfileImage(null);
      }
    } catch (error) {
      console.error("Ошибка при загрузке данных пользователя:", error);
      toast.error("Не удалось загрузить данные пользователя.");
    }
  }, [navigate]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  const formatPhoneNumber = (value) => {
    let digitsOnly = value.replace(/\D/g, "");
    if (digitsOnly.length > 11) {
      digitsOnly = digitsOnly.slice(0, 11);
    }

    let formatted = "";
    if (digitsOnly.startsWith("7")) {
      formatted = "+7";
      if (digitsOnly.length > 1) {
        formatted += " (" + digitsOnly.slice(1, 4);
      }
      if (digitsOnly.length >= 5) {
        formatted += ") " + digitsOnly.slice(4, 7);
      }
      if (digitsOnly.length >= 8) {
        formatted += "-" + digitsOnly.slice(7, 9);
      }
      if (digitsOnly.length >= 10) {
        formatted += "-" + digitsOnly.slice(9, 11);
      }
    } else {
      formatted = digitsOnly;
    }

    return formatted;
  };

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [id]: id === "phone" ? formatPhoneNumber(value) : value,
    }));
  };

  const updateUserData = async () => {
    try {
      const payload = {
        token: localStorage.getItem("token"),
        surname: formData.lastName || "",
        name: formData.firstName || "",
        organization: "",
        city: "",
        gId: formData.gId || "",
        phoneNumber: formData.phone || "",
        email: localStorage.getItem("auth_email") || "",
      };

      await axios.post(
        "https://us-central1-extent-protocol-a.cloudfunctions.net/changeUserDataByEmail",
        payload
      );

      toast.success("Данные успешно обновлены!");

      fetchUserData();
    } catch (error) {
      console.error("Ошибка при обновлении данных:", error);
      toast.error("Не удалось обновить данные.");
    }
  };

  useEffect(() => {
    if (location.state?.fromTrial) {
    }
  }, [location.state]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const requiredFields = ["firstName", "lastName", "gId", "phone"];

    const empty = requiredFields.filter((key) => !formData[key]);

    setEmptyFields(empty);

    if (empty.length > 0) {
      toast.error("Заполните все обязательные поля!");
      return;
    }

    await updateUserData();
  };

  const handleImageUpload = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";

    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (file) {
        if (file.type.startsWith("image/")) {
          const currentUserId = localStorage.getItem("userId");
          if (!currentUserId) {
            toast.error(
              "Не удалось найти userId. Пожалуйста, перезайдите в систему."
            );
            return;
          }

          const renamedFile = new File([file], `${currentUserId}.png`, {
            type: file.type,
          });

          const storageRef = ref(storage, `pictures/${renamedFile.name}`);
          const uploadTask = uploadBytesResumable(storageRef, renamedFile);

          uploadTask.on(
            "state_changed",
            (snapshot) => {
              const progress = Math.round(
                (snapshot.bytesTransferred / snapshot.totalBytes) * 100
              );
              setUploadProgress(progress);
            },
            (error) => {
              console.error("Ошибка загрузки:", error);
              toast.error("Не удалось загрузить изображение.");
            },
            async () => {
              const url = await getDownloadURL(uploadTask.snapshot.ref);
              setProfileImage(url);

              const userProfile =
                JSON.parse(localStorage.getItem("user_profile")) || {};
              userProfile.profileImage = url;
              localStorage.setItem("user_profile", JSON.stringify(userProfile));

              window.dispatchEvent(new CustomEvent("profileImageUpdated"));

              toast.success("Фото успешно загружено!");
            }
          );
        } else {
          toast.error("Пожалуйста, выберите файл изображения.");
        }
      }
    };

    input.click();
  };

  const handleDeleteImage = async () => {
    try {
      const currentUserId = localStorage.getItem("userId");
      if (!currentUserId) {
        toast.error("Не найден userId для удаления изображения.");
        return;
      }

      const fileName = currentUserId + ".png";
      const imageRef = ref(storage, `pictures/${fileName}`);
      await deleteObject(imageRef);
      setProfileImage(null);
      localStorage.removeItem("profileImage");
      toast.success("Изображение успешно удалено.");
    } catch (error) {
      console.error("Ошибка удаления изображения:", error);
      toast.error("Не удалось удалить изображение.");
    }
  };

  return (
    <div className="profile">
      <nav className="profile__navigation">
        <Link
          className={`profile__nav-link ${
            location.pathname === "/profile" ? "profile__nav-link--active" : ""
          }`}
          to="/profile"
        >
          Профиль
        </Link>
        <Link
          className={`profile__nav-link ${
            location.pathname === "/profile/security"
              ? "profile__nav-link--active"
              : ""
          }`}
          to="/profile/security"
        >
          Безопасность
        </Link>
      </nav>
      <p className="profile__section-title">Настройка профиля</p>
      <br />
      <div className="profile__row">
        <div className="profile__column">
          <div className="profile__card">
            <div className="profile__card-header">Фото профиля</div>
            <div className="profile__card-body">
              {profileImage ? (
                <img
                  src={profileImage}
                  alt="Profile Preview"
                  className="profile__image-preview"
                  style={{
                    width: "80px",
                    height: "80px",
                    borderRadius: "50%",
                  }}
                />
              ) : (
                <FaUserAlt color="#778" className="profile__image-preview" />
              )}
              <p className="profile__help-text">Загрузить/поменять фото</p>

              <button
                className="profile__button profile__button--upload"
                onClick={handleImageUpload}
              >
                Загрузить
              </button>
              <button
                className="profile__button profile__button--delete"
                onClick={handleDeleteImage}
              >
                Удалить
              </button>

              {uploadProgress > 0 && uploadProgress < 100 && (
                <div className="profile__loader-container">
                  <div className="profile__loader"></div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="profile__column">
          <div className="profile__card">
            <div className="profile__card-header">Информация</div>
            <div className="profile__card-body">
              <form onSubmit={handleSubmit}>
                <div className="profile__form-group"></div>
                <div className="profile__row">
                  <div className="profile__form-group">
                    <label htmlFor="inputFirstName">Имя</label>
                    <input
                      id="firstName"
                      type="text"
                      placeholder="Имя"
                      value={formData.firstName}
                      required
                      onChange={handleChange}
                      className={
                        emptyFields.includes("firstName")
                          ? "input-alert-error"
                          : ""
                      }
                    />
                  </div>
                  <div className="profile__form-group">
                    <label htmlFor="inputLastName">Фамилия</label>
                    <input
                      id="lastName"
                      type="text"
                      placeholder="Фамилия"
                      value={formData.lastName}
                      required
                      onChange={handleChange}
                      className={
                        emptyFields.includes("lastName")
                          ? "input-alert-error"
                          : ""
                      }
                    />
                  </div>
                </div>
                <div className="profile__row">
                  <div className="profile__form-group">
                    <label htmlFor="inputPhone">Корпоративный ID</label>
                    <input
                      id="gId"
                      type="text"
                      placeholder="Корпоративный ID"
                      value={formData.gId}
                      disabled
                      required
                      onChange={handleChange}
                      className={
                        emptyFields.includes("gId") ? "input-alert-error" : ""
                      }
                    />
                  </div>
                  <div className="profile__form-group">
                    <label htmlFor="inputPhone">Номер телефона*</label>
                    <input
                      id="phone"
                      type="tel"
                      placeholder="+7 (777) 777-07-07"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className={
                        emptyFields.includes("phone") ? "input-alert-error" : ""
                      }
                    />
                  </div>
                </div>
                <button
                  className="profile__button profile__button--save"
                  type="submit"
                >
                  Сохранить
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
